/**
 * QueryBuilder a CANVAS (ADR-0063 P5 — la "faccia visuale" del MODELLO della query).
 *
 * Un canvas ReactFlow che autora un Dataset globale come query salvata: ogni
 * relazione (primario + ogni target di `config.model.joins`) è una card con
 * le sue colonne; trascini da una colonna all'altra per creare una condizione ON;
 * l'arco porta il tipo di join. È una PROIEZIONE dello stesso `config.model` che il
 * backend persiste — riusa `apply_dataset_model` / `validate_dataset_model` /
 * `_query_model` (preview). Se le sorgenti unite stanno sulla STESSA sorgente il
 * motore fa un JOIN nativo pushato; se sono DIVERSE le federa in automatico —
 * l'autore non sceglie: "federazione" è un dettaglio del motore, non del concetto.
 *
 * Il modello spec↔grafo puro è in `lib/datasets/queryGraph.ts` (unit-testato); qui
 * c'è solo il canvas + il fetch delle colonne + la preview. Il look&feel lo verifica
 * il proprietario a occhio (il canvas è nel tier di test differito, come Mappa/ER).
 */
import * as React from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
} from "reactflow";
import "reactflow/dist/style.css";
import { Combine, Loader2, Play, Plus, Rows3, Trash2 } from "lucide-react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import {
  appsApi,
  type AppSummary,
  type ColumnOut,
  type LinkedResource,
  type RowsPage,
  type TableDetail,
} from "@/lib/api-apps";
import {
  datasetsApi,
  type Dataset,
  type DatasetModelJoin,
  type DatasetModelSpec,
  type DatasetModelUnfold,
  type DatasetModelUnion,
  type DatasetPreview,
} from "@/lib/api-datasets";
import { sourcesApi } from "@/lib/api-sources";
import {
  PRIMARY_ID,
  addUnfold,
  addUnion,
  buildEdges,
  buildModelSpec,
  buildNodes,
  connectToJoins,
  removeJoin,
  removeOn,
  removeUnfold,
  removeUnion,
  setJoinType,
  setUnfoldKeepEmpty,
  setUnionColumn,
  setUnionType,
  shapeFromModel,
  unfoldFromModel,
  unionsFromModel,
  type QbAgg,
  type QbFieldRef,
  type QbFilter,
  type QbNode,
  type QbShape,
  type QbSort,
} from "@/lib/datasets/queryGraph";
import {
  BASE_ALIAS,
  addDatasetJoin,
  addRelationJoin,
  addUnfold as appAddUnfold,
  addUnion as appAddUnion,
  appEditFromConfig,
  appEditToConfig,
  federationLeftKeys,
  outputKeys,
  patchDatasetJoin,
  removeDatasetJoin,
  removeJoinCascade,
  removeUnfold as appRemoveUnfold,
  removeUnion as appRemoveUnion,
  resolveAppAliasTables,
  selectedFieldsFor,
  setUnfoldKeepEmpty as appSetUnfoldKeepEmpty,
  setUnionColumn as appSetUnionColumn,
  setUnionType as appSetUnionType,
  toggleDatasetColumn,
  toggleSelectColumn,
  type AppEditModel,
  type AppModelAgg,
  type AppModelFilter,
} from "@/lib/datasets/appModelGraph";

const HEADER_H = 30;
const ROW_H = 22;
const NODE_W = 190;
const HANDLE = "!h-2 !w-2 !min-w-0 !border !border-border !bg-elevated";

/** ReactFlow custom-node payload built by the canvas (columns + callbacks). */
interface QbNodeData {
  node: QbNode;
  onType: (joinIndex: number, type: "inner" | "left") => void;
  onRemove: (joinIndex: number) => void;
  /** Field names of THIS relation currently in the projection (`select`). */
  selected: Set<string>;
  /** Toggle a column in/out of the projection (DevExpress-style, in the card). */
  onToggleColumn: (field: string) => void;
  /** App-mode: joins are RELATION-FOLLOW (structural ON), so there's no arbitrary
   *  drag-ON — the per-column handles are hidden and the card is checkboxes only. */
  hideHandles?: boolean;
}

/** A relation card: header (name + join-type toggle + remove) + a row per column.
 *  Each column carries a projection CHECKBOX (DevExpress-style — ticking it adds the
 *  column to the output `select`) + a LEFT target handle and a RIGHT source handle so
 *  you can draw an ON from any column to any column (direction is normalised in
 *  `connectToJoins`). */
function QbTableNode({ data }: NodeProps<QbNodeData>) {
  const { node, onType, onRemove, selected, onToggleColumn, hideHandles } = data;
  const isJoin = node.joinIndex !== null;
  return (
    <div className="rounded-md border border-border bg-surface text-xs shadow-sm" style={{ width: NODE_W }}>
      {/* App mode hides the per-column handles, but the node still needs NODE-LEVEL
          anchors or ReactFlow can't draw the relation/federation edges. */}
      {hideHandles && (
        <>
          <Handle id="in" type="target" position={Position.Left} className={HANDLE} style={{ top: HEADER_H / 2 }} />
          <Handle id="out" type="source" position={Position.Right} className={HANDLE} style={{ top: HEADER_H / 2 }} />
        </>
      )}
      <div
        className="flex items-center gap-1 rounded-t-md border-b border-border bg-elevated px-2 font-medium"
        style={{ height: HEADER_H }}
      >
        <span className="truncate flex-1" title={node.label}>
          {node.label}
        </span>
        {isJoin && (
          <>
            <select
              className="nodrag h-5 rounded border border-border bg-background px-1 text-[10px]"
              value={node.type ?? "inner"}
              onChange={(e) => onType(node.joinIndex!, e.target.value as "inner" | "left")}
            >
              <option value="left">LEFT</option>
              <option value="inner">INNER</option>
            </select>
            <button
              className="nodrag text-muted-foreground hover:text-danger"
              title="Rimuovi"
              onClick={() => onRemove(node.joinIndex!)}
            >
              <Trash2 className="h-3 w-3" strokeWidth={1.5} />
            </button>
          </>
        )}
      </div>
      <div className="py-0.5">
        {node.columns.length === 0 && (
          <div className="px-2 text-[10px] italic text-muted-foreground" style={{ height: ROW_H, lineHeight: `${ROW_H}px` }}>
            (nessuna colonna)
          </div>
        )}
        {node.columns.map((c) => (
          <div
            key={c}
            className="relative flex items-center gap-1.5 px-2"
            style={{ height: ROW_H }}
          >
            {!hideHandles && (
              <Handle
                id={`t:${c}`}
                type="target"
                position={Position.Left}
                className={HANDLE}
                style={{ top: "50%" }}
              />
            )}
            {/* DevExpress-style projection checkbox, in the card next to the column. */}
            <input
              type="checkbox"
              className="nodrag h-3 w-3 shrink-0 cursor-pointer accent-[hsl(var(--accent))]"
              checked={selected.has(c)}
              onChange={() => onToggleColumn(c)}
              title="Includi nell'output"
            />
            <span
              className={"flex-1 cursor-pointer truncate " + (selected.has(c) ? "" : "text-muted-foreground")}
              title={c}
              onClick={() => onToggleColumn(c)}
            >
              {c}
            </span>
            {!hideHandles && (
              <Handle
                id={`s:${c}`}
                type="source"
                position={Position.Right}
                className={HANDLE}
                style={{ top: "50%" }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Payload for a FEDERATED linked-dataset canvas node (part 2). */
interface FederatedNodeData {
  datasetId: string;
  label: string;
  fields: string[];
  picked: Set<string>;
  type: "inner" | "left";
  on: { left: string; right: string }[];
  leftKeys: string[];
  canWrite: boolean;
  onToggleCol: (field: string) => void;
  onType: (t: "inner" | "left") => void;
  onRemove: () => void;
  onPatchOn: (on: { left: string; right: string }[]) => void;
}

/** A LINKED-dataset federation node: header (name + join-type + remove) → the ON
 *  editor (a projection column = a dataset field) → a scrollable column checklist.
 *  Lives ON the canvas (with an edge to the base) so the federation is visible, and
 *  the columns are scrollable instead of a flat wall. */
function FederatedDatasetNode({ data }: NodeProps<FederatedNodeData>) {
  const d = data;
  const sel = "nodrag h-5 w-full rounded border border-border bg-background px-1 text-[10px]";
  return (
    <div className="rounded-md border border-accent/60 bg-surface text-xs shadow-sm" style={{ width: 250 }}>
      <Handle id="in" type="target" position={Position.Left} className={HANDLE} style={{ top: HEADER_H / 2 }} />
      <div
        className="flex items-center gap-1 rounded-t-md border-b border-border bg-elevated px-2 font-medium"
        style={{ height: HEADER_H }}
      >
        <Combine className="h-3 w-3 shrink-0 text-accent" strokeWidth={1.5} />
        <span className="flex-1 truncate" title={d.label}>
          {d.label}
        </span>
        <select
          className="nodrag h-5 rounded border border-border bg-background px-1 text-[10px]"
          value={d.type}
          disabled={!d.canWrite}
          onChange={(e) => d.onType(e.target.value as "inner" | "left")}
        >
          <option value="left">LEFT</option>
          <option value="inner">INNER</option>
        </select>
        {d.canWrite && (
          <button className="nodrag text-muted-foreground hover:text-danger" title="Rimuovi" onClick={d.onRemove}>
            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
          </button>
        )}
      </div>

      <div className="space-y-1 border-b border-border p-1.5">
        <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Abbina</span>
        {d.on.map((o, i) => (
          <div key={i} className="flex items-center gap-1">
            <select
              className={sel + " flex-1"}
              value={o.left}
              disabled={!d.canWrite}
              onChange={(e) => d.onPatchOn(d.on.map((x, k) => (k === i ? { ...x, left: e.target.value } : x)))}
            >
              <option value="">colonna…</option>
              {d.leftKeys.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-muted-foreground">=</span>
            <select
              className={sel + " flex-1"}
              value={o.right}
              disabled={!d.canWrite}
              onChange={(e) => d.onPatchOn(d.on.map((x, k) => (k === i ? { ...x, right: e.target.value } : x)))}
            >
              <option value="">campo…</option>
              {d.fields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            {d.canWrite && d.on.length > 1 && (
              <button
                className="nodrag text-muted-foreground hover:text-danger"
                onClick={() => d.onPatchOn(d.on.filter((_, k) => k !== i))}
              >
                <Trash2 className="h-3 w-3" strokeWidth={1.5} />
              </button>
            )}
          </div>
        ))}
        {d.canWrite && (
          <button
            className="nodrag flex items-center gap-1 text-[10px] text-accent"
            onClick={() => d.onPatchOn([...d.on, { left: "", right: "" }])}
          >
            <Plus className="h-2.5 w-2.5" strokeWidth={1.5} /> condizione
          </button>
        )}
      </div>

      <div className="max-h-40 overflow-auto py-0.5">
        {d.fields.length === 0 && (
          <div className="px-2 text-[10px] italic text-muted-foreground" style={{ height: ROW_H, lineHeight: `${ROW_H}px` }}>
            (campi non caricati)
          </div>
        )}
        {d.fields.map((f) => (
          <label key={f} className="flex cursor-pointer items-center gap-1.5 px-2" style={{ height: ROW_H }}>
            <input
              type="checkbox"
              className="nodrag h-3 w-3 shrink-0 cursor-pointer accent-[hsl(var(--accent))]"
              checked={d.picked.has(f)}
              disabled={!d.canWrite}
              onChange={() => d.onToggleCol(f)}
            />
            <span className={"flex-1 truncate " + (d.picked.has(f) ? "" : "text-muted-foreground")} title={f}>
              {f}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

const nodeTypes = { qbTable: QbTableNode, qbFederated: FederatedDatasetNode };

/** Resolve the columns of ONE join target: a dataset via `/fields`, an app-table
 *  Entity via listTables→getTable. Returns `[]` on any miss (the node still renders,
 *  the operator just can't draw an ON until it loads). */
async function fetchJoinColumns(
  j: DatasetModelJoin,
  datasetIdByName: Map<string, string>,
): Promise<string[]> {
  if (j.source === "entity") {
    if (!j.app_id) return [];
    const tables = await appsApi.listTables(j.app_id);
    const t = tables.find((x) => x.name === j.dataset);
    if (!t) return [];
    const detail = await appsApi.getTable(j.app_id, t.id);
    return detail.columns.map((c) => c.name);
  }
  const id = datasetIdByName.get(j.dataset);
  if (!id) return [];
  return (await datasetsApi.fields(id)).map((f) => f.name);
}

const FILTER_OPS = [
  "eq",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains",
  "starts_with",
  "ends_with",
  "in",
  "is_null",
  "is_not_null",
];
const AGG_FNS = ["count", "sum", "avg", "min", "max"];
const BUCKETS = ["", "day", "week", "month", "quarter", "year"];
type ShapeTab = "filters" | "group" | "sort";

type QbColumn = { key: string; dataset?: string; field: string };

/** The Colonne · Filtri · Raggruppa · Ordina panel — authors the query SHAPE (F2–F5)
 *  as a projection of `config.model`. Controlled by the parent (`shape` +
 *  `patch`), so the whole thing round-trips through `buildModelSpec`. */
function ShapePanel({
  shape,
  patch,
  columns,
  aliases,
}: {
  shape: QbShape;
  patch: (p: Partial<QbShape>) => void;
  columns: QbColumn[];
  aliases: string[];
}) {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState<ShapeTab>("filters");
  const nsel = "h-7 rounded border border-border bg-background px-1 text-xs";
  const colByKey = (k: string) => columns.find((c) => c.key === k);
  const keyOf = (r: { dataset?: string; field: string }) =>
    r.dataset ? `${r.dataset}.${r.field}` : r.field;
  const asRef = (c: QbColumn): QbFieldRef => (c.dataset ? { dataset: c.dataset, field: c.field } : { field: c.field });
  const same = (a: { dataset?: string; field: string }, b: QbColumn) =>
    (a.dataset || "") === (b.dataset || "") && a.field === b.field;

  const tabs: { id: ShapeTab; label: string }[] = [
    { id: "filters", label: t("datasets.qbTabFilters", "Filtri") },
    { id: "group", label: t("datasets.qbTabGroup", "Raggruppa") },
    { id: "sort", label: t("datasets.qbTabSort", "Ordina") },
  ];

  return (
    <div className="flex w-64 shrink-0 flex-col rounded-md border border-border">
      <div className="flex shrink-0 border-b border-border">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            className={
              "flex-1 border-b-2 px-1 py-1.5 text-[11px] " +
              (tab === tb.id ? "border-accent font-medium" : "border-transparent text-muted-foreground")
            }
            onClick={() => setTab(tb.id)}
          >
            {tb.label}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 space-y-1.5 overflow-auto p-2 text-xs">
        {tab === "group" && (
          <>
            <p className="text-[10px] font-medium text-muted-foreground">
              {t("datasets.qbGroupBy", "Raggruppa per")}
            </p>
            {columns.map((c) => {
              const gi = shape.group_by.findIndex((g) => same(g, c));
              return (
                <div key={c.key} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={gi >= 0}
                    onChange={() =>
                      patch({
                        group_by:
                          gi >= 0
                            ? shape.group_by.filter((g) => !same(g, c))
                            : [...shape.group_by, asRef(c)],
                      })
                    }
                  />
                  <span className="flex-1 truncate">{c.key}</span>
                  {gi >= 0 && (
                    <select
                      className={nsel}
                      value={shape.group_by[gi].bucket ?? ""}
                      onChange={(e) =>
                        patch({
                          group_by: shape.group_by.map((g, k) =>
                            k === gi
                              ? { ...g, bucket: (e.target.value || undefined) as QbFieldRef["bucket"] }
                              : g,
                          ),
                        })
                      }
                      title={t("datasets.qbBucket", "Periodo (per date)")}
                    >
                      {BUCKETS.map((b) => (
                        <option key={b} value={b}>
                          {b || "—"}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              );
            })}
            <p className="pt-1 text-[10px] font-medium text-muted-foreground">
              {t("datasets.qbAggregations", "Aggregazioni")}
            </p>
            {shape.aggregations.map((a, i) => (
              <div key={i} className="flex items-center gap-1">
                <select
                  className={nsel}
                  value={a.fn}
                  onChange={(e) =>
                    patch({ aggregations: shape.aggregations.map((x, k) => (k === i ? { ...x, fn: e.target.value } : x)) })
                  }
                >
                  {AGG_FNS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                {a.fn !== "count" && (
                  <select
                    className={nsel + " flex-1"}
                    value={a.field ? keyOf({ field: a.field }) : ""}
                    onChange={(e) => {
                      const c = colByKey(e.target.value);
                      patch({
                        aggregations: shape.aggregations.map((x, k) =>
                          k === i ? { ...x, field: c?.field } : x,
                        ),
                      });
                    }}
                  >
                    <option value="">—</option>
                    {columns.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.key}
                      </option>
                    ))}
                  </select>
                )}
                <Input
                  className="h-7 w-16"
                  placeholder="alias"
                  value={a.as ?? ""}
                  onChange={(e) =>
                    patch({ aggregations: shape.aggregations.map((x, k) => (k === i ? { ...x, as: e.target.value || undefined } : x)) })
                  }
                />
                <button
                  className="text-muted-foreground hover:text-danger"
                  onClick={() => patch({ aggregations: shape.aggregations.filter((_, k) => k !== i) })}
                >
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patch({ aggregations: [...shape.aggregations, { fn: "count", as: "conteggio" } as QbAgg] })}
            >
              <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
              {t("datasets.qbAddAgg", "Aggregazione")}
            </Button>
          </>
        )}

        {tab === "filters" && (
          <>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-muted-foreground">{t("datasets.qbMatch", "Corrispondenza")}</span>
              <select
                className={nsel}
                value={shape.match}
                onChange={(e) => patch({ match: e.target.value as "all" | "any" })}
              >
                <option value="all">{t("datasets.qbMatchAll", "Tutte (E)")}</option>
                <option value="any">{t("datasets.qbMatchAny", "Almeno una (O)")}</option>
              </select>
            </div>
            {shape.filters.map((f, i) => (
              <div key={i} className="flex items-center gap-1">
                <select
                  className={nsel + " flex-1"}
                  value={keyOf(f)}
                  onChange={(e) => {
                    const c = colByKey(e.target.value);
                    patch({
                      filters: shape.filters.map((x, k) =>
                        k === i ? { ...x, dataset: c?.dataset, field: c?.field ?? "" } : x,
                      ),
                    });
                  }}
                >
                  <option value="">—</option>
                  {columns.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.key}
                    </option>
                  ))}
                </select>
                <select
                  className={nsel}
                  value={f.op}
                  onChange={(e) => patch({ filters: shape.filters.map((x, k) => (k === i ? { ...x, op: e.target.value } : x)) })}
                >
                  {FILTER_OPS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                {f.op !== "is_null" && f.op !== "is_not_null" && (
                  <Input
                    className="h-7 w-16"
                    value={(f.value as string | number | undefined) ?? ""}
                    onChange={(e) => patch({ filters: shape.filters.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)) })}
                  />
                )}
                <button
                  className="text-muted-foreground hover:text-danger"
                  onClick={() => patch({ filters: shape.filters.filter((_, k) => k !== i) })}
                >
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patch({ filters: [...shape.filters, { field: columns[0]?.field ?? "", op: "eq", value: "" } as QbFilter] })}
            >
              <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
              {t("datasets.qbAddFilter", "Filtro")}
            </Button>
          </>
        )}

        {tab === "sort" && (
          <>
            <p className="text-[10px] font-medium text-muted-foreground">{t("datasets.qbOrderBy", "Ordina per")}</p>
            {shape.order_by.map((o, i) => (
              <div key={i} className="flex items-center gap-1">
                <select
                  className={nsel + " flex-1"}
                  value={o.by}
                  onChange={(e) => patch({ order_by: shape.order_by.map((x, k) => (k === i ? { ...x, by: e.target.value } : x)) })}
                >
                  <option value="">—</option>
                  {aliases.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
                <select
                  className={nsel}
                  value={o.dir}
                  onChange={(e) => patch({ order_by: shape.order_by.map((x, k) => (k === i ? { ...x, dir: e.target.value as "asc" | "desc" } : x)) })}
                >
                  <option value="asc">ASC</option>
                  <option value="desc">DESC</option>
                </select>
                <button
                  className="text-muted-foreground hover:text-danger"
                  onClick={() => patch({ order_by: shape.order_by.filter((_, k) => k !== i) })}
                >
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patch({ order_by: [...shape.order_by, { by: aliases[0] ?? "", dir: "asc" } as QbSort] })}
            >
              <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
              {t("datasets.qbAddSort", "Ordinamento")}
            </Button>
            <p className="pt-2 text-[10px] font-medium text-muted-foreground">{t("datasets.qbSearch", "Ricerca libera")}</p>
            <Input
              className="h-7"
              value={shape.search}
              placeholder={t("datasets.qbSearchPh", "testo…")}
              onChange={(e) => patch({ search: e.target.value })}
            />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The ONE query-builder component (ADR-0063). It DISPATCHES on `appContext`: with it,
 * an APP PROJECTION canvas (relation-follow joins over app tables, governed read,
 * mounted inline in the builder); without it, the GLOBAL dataset canvas (federation
 * over catalog sources, in a dialog). Both author the unified `config.model` + share
 * the card node + shape panel + preview, so it's one editor for the user.
 */
export function DatasetQueryBuilder(props: {
  /** null = CREATE (global). A Dataset = EDIT (global). Ignored in app mode. */
  dataset?: Dataset | null;
  open?: boolean;
  onClose?: () => void;
  /** Set ⇒ APP-PROJECTION mode: edit the app dataset `datasetId` of `appId` inline. */
  appContext?: { appId: string; datasetId: string; canWrite: boolean };
}) {
  if (props.appContext) {
    return <AppDatasetCanvas {...props.appContext} />;
  }
  return (
    <GlobalDatasetCanvas
      dataset={props.dataset ?? null}
      open={props.open ?? false}
      onClose={props.onClose ?? (() => {})}
    />
  );
}

function GlobalDatasetCanvas({
  dataset,
  open,
  onClose,
}: {
  /** null = CREATE mode (canvas-first: pick the primary source in the canvas, POST
   *  on Save). A Dataset = EDIT mode (PATCH the model). */
  dataset: Dataset | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isCreate = dataset === null;

  // CREATE-mode state: the primary relation is a source + schema + table picked IN the
  // canvas (Qlik/Power BI-style), plus the dataset name — the object is born on Save.
  const [name, setName] = React.useState("");
  const [sourceId, setSourceId] = React.useState<string | null>(null);
  const [schema, setSchema] = React.useState<string | null>(null);
  const [table, setTable] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (table && !name) setName(table);
  }, [table]); // eslint-disable-line react-hooks/exhaustive-deps
  // The primary is "ready" (its card can render) once we know its columns: in EDIT the
  // dataset already exists; in CREATE it needs a source + schema + table.
  const primaryReady = !isCreate || (!!sourceId && !!schema && !!table);
  const primaryName = isCreate ? name.trim() || table || "Nuovo dataset" : dataset!.name;

  const seeded = (dataset?.config?.model ?? undefined) as DatasetModelSpec | undefined;
  const [joins, setJoins] = React.useState<DatasetModelJoin[]>(
    () => seeded?.joins?.map((j) => ({ ...j, on: (j.on ?? []).map((o) => ({ ...o })) })) ?? [],
  );
  // The authored query SHAPE (F2–F5): select · group/agg · filters · order · search.
  const [shape, setShape] = React.useState<QbShape>(() =>
    shapeFromModel(seeded as Record<string, unknown> | undefined),
  );
  const patchShape = (p: Partial<QbShape>) => setShape((s) => ({ ...s, ...p }));

  // UNION branches (ADR-0064 P5) — STACK other relations' rows under the primary's,
  // authored in a dedicated section (not a join edge — a union maps columns, not keys).
  const [unions, setUnions] = React.useState<DatasetModelUnion[]>(() =>
    unionsFromModel(seeded as Record<string, unknown> | undefined),
  );

  // UNFOLD (ADR-0064 P5 Transformation) — EXPLODE a JSON-array column into rows.
  const [unfold, setUnfold] = React.useState<DatasetModelUnfold[]>(() =>
    unfoldFromModel(seeded as Record<string, unknown> | undefined),
  );

  // DevExpress-style projection: a checkbox per column IN the canvas card toggles it
  // in/out of `shape.select`. A relation's qualifier is its dataset name (undefined for
  // the primary), matching how `allColumns` / `ShapePanel` key a `QbFieldRef`.
  const sameRef = (r: { dataset?: string; field: string }, ds: string | undefined, f: string) =>
    (r.dataset || "") === (ds || "") && r.field === f;
  const toggleSelect = (ds: string | undefined, field: string) =>
    setShape((s) => ({
      ...s,
      select: s.select.some((r) => sameRef(r, ds, field))
        ? s.select.filter((r) => !sameRef(r, ds, field))
        : [...s.select, ds ? { dataset: ds, field } : { field }],
    }));
  const selectedFieldsFor = (ds: string | undefined) =>
    new Set(shape.select.filter((r) => (r.dataset || "") === (ds || "")).map((r) => r.field));

  // Catalog: published global datasets (join targets + name→id) + apps (entity targets).
  const listQ = useQuery({ queryKey: ["datasets", "list"], queryFn: () => datasetsApi.list(), enabled: open });
  const appsQ = useQuery({ queryKey: ["apps", "list"], queryFn: () => appsApi.list(), enabled: open });
  const datasetIdByName = React.useMemo(
    () => new Map((listQ.data ?? []).filter((d) => d.name).map((d) => [d.name, d.id])),
    [listQ.data],
  );
  const targets = (listQ.data ?? []).filter(
    (d) => d.id !== (dataset?.id ?? "") && d.published && d.name,
  );
  const apps = appsQ.data ?? [];

  // CREATE mode: pick the primary source → drill its schemas → tables (the same
  // `sources/{id}/introspect` drill the old create dialog used, now inline in the canvas).
  const sourcesQ = useQuery({
    queryKey: ["sources", "external"],
    queryFn: () => sourcesApi.list({ kind: "external" }),
    enabled: open && isCreate,
  });
  const schemasQ = useQuery({
    queryKey: ["sources", sourceId, "introspect", "schemas"],
    queryFn: () => sourcesApi.introspect(sourceId!, { level: "schemas" }),
    enabled: open && isCreate && !!sourceId,
  });
  const tablesQ = useQuery({
    queryKey: ["sources", sourceId, "introspect", "tables", schema],
    queryFn: () => sourcesApi.introspect(sourceId!, { level: "tables", schema: schema! }),
    enabled: open && isCreate && !!sourceId && !!schema,
  });

  // Columns of the PRIMARY relation: in EDIT via the dataset's `/fields`; in CREATE via
  // the source's column introspection (no dataset exists yet — that's the whole point).
  const primaryFieldsQ = useQuery({
    queryKey: isCreate
      ? ["qb-primary-cols", sourceId, schema, table]
      : ["datasets", dataset!.id, "fields"],
    queryFn: async () => {
      if (isCreate) {
        const r = await sourcesApi.introspect(sourceId!, { level: "columns", schema: schema!, table: table! });
        return r.columns.map((c) => ({ name: c.name, type: c.type }));
      }
      return datasetsApi.fields(dataset!.id);
    },
    enabled: open && (!isCreate || (!!sourceId && !!schema && !!table)),
  });
  const targetColsQ = useQueries({
    queries: joins.map((j) => ({
      queryKey: ["qb-cols", j.source ?? "dataset", j.app_id ?? "", j.dataset],
      queryFn: () => fetchJoinColumns(j, datasetIdByName),
      enabled: open && (j.source !== "entity" || !!j.app_id) && (j.source === "entity" || datasetIdByName.size > 0),
    })),
  });
  const columnsById = React.useMemo(() => {
    const m: Record<string, string[]> = {
      [PRIMARY_ID]: (primaryFieldsQ.data ?? []).map((f) => f.name),
    };
    joins.forEach((j, i) => {
      m[j.dataset] = (targetColsQ[i]?.data as string[] | undefined) ?? [];
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryFieldsQ.data, joins, targetColsQ.map((q) => q.data).join("|")]);

  // Every addressable column (primary + each join), qualified — the source of every
  // column dropdown in the shape panels.
  const allColumns = React.useMemo(() => {
    const out: { key: string; dataset?: string; field: string }[] = [];
    (columnsById[PRIMARY_ID] ?? []).forEach((f) => out.push({ key: f, field: f }));
    joins.forEach((j) =>
      (columnsById[j.dataset] ?? []).forEach((f) =>
        out.push({ key: `${j.dataset}.${f}`, dataset: j.dataset, field: f }),
      ),
    );
    return out;
  }, [columnsById, joins]);

  // The output aliases available to `order_by` — the grouped/agg aliases when
  // grouping, else the projected aliases, else all primary columns.
  const outputAliases = React.useMemo(() => {
    if (shape.group_by.length || shape.aggregations.length) {
      return [
        ...shape.group_by.map((g) => g.as || g.field),
        ...shape.aggregations.map((a) => a.as || a.fn),
      ];
    }
    if (shape.select.length) return shape.select.map((s) => s.as || s.field);
    return columnsById[PRIMARY_ID] ?? [];
  }, [shape, columnsById]);

  // Build the ReactFlow nodes/edges from the spec (positions: primary left, joins stacked).
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  // Live nodes ref so a rebuild (columns loaded / a join added) preserves any
  // position the operator dragged a card to.
  const rfNodesRef = React.useRef<Node<QbNodeData>[]>([]);
  rfNodesRef.current = rfNodes;
  React.useEffect(() => {
    const qbNodes = buildNodes(primaryName, joins, columnsById);
    const nodes: Node<QbNodeData>[] = qbNodes.map((n) => {
      const dragged = rfNodesRef.current.find((e) => e.id === n.id)?.position;
      // The primary card has no dataset qualifier; a join card's qualifier is its
      // dataset name (used to key the projection ref, like `allColumns`).
      const ds = n.joinIndex === null ? undefined : joins[n.joinIndex]?.dataset;
      return {
        id: n.id,
        type: "qbTable",
        position:
          dragged ??
          (n.joinIndex === null ? { x: 20, y: 20 } : { x: 320, y: 20 + n.joinIndex * 200 }),
        data: {
          node: n,
          onType: (ji: number, type: "inner" | "left") => setJoins((js) => setJoinType(js, ji, type)),
          onRemove: (ji: number) => setJoins((js) => removeJoin(js, ji)),
          selected: selectedFieldsFor(ds),
          onToggleColumn: (field: string) => toggleSelect(ds, field),
        },
      };
    });
    const edges: Edge[] = buildEdges(joins).map((e) => ({
      id: e.id,
      source: e.source,
      sourceHandle: `s:${e.sourceCol}`,
      target: e.target,
      targetHandle: `t:${e.targetCol}`,
      label: `${e.sourceCol} = ${e.targetCol}`,
      labelStyle: { fontSize: 9 },
    }));
    setRfNodes(nodes);
    setRfEdges(edges);
    // `shape.select` is a dep so the in-card checkboxes reflect the current projection
    // (positions are preserved via `rfNodesRef`; edges rebuild identically from joins).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joins, columnsById, primaryName, shape.select]);

  const onConnect = React.useCallback(
    (c: Connection) => {
      if (!c.source || !c.target || !c.sourceHandle || !c.targetHandle) return;
      const next = connectToJoins(joins, {
        sourceNode: c.source,
        sourceCol: c.sourceHandle.replace(/^s:/, ""),
        targetNode: c.target,
        targetCol: c.targetHandle.replace(/^t:/, ""),
      });
      if (next) setJoins(next);
    },
    [joins],
  );

  const onEdgesDelete = React.useCallback((deleted: Edge[]) => {
    setJoins((js) => {
      let out = js;
      for (const e of deleted) {
        const m = /^on:(\d+):(\d+)$/.exec(e.id);
        if (m) out = removeOn(out, Number(m[1]), Number(m[2]));
      }
      return out;
    });
  }, []);

  // Add-target toolbar state.
  const [addKind, setAddKind] = React.useState<"dataset" | "entity">("dataset");
  const [addApp, setAddApp] = React.useState("");
  const addTablesQ = useQuery({
    queryKey: ["apps", addApp, "tables"],
    queryFn: () => appsApi.listTables(addApp),
    enabled: open && addKind === "entity" && !!addApp,
  });
  const [addTarget, setAddTarget] = React.useState("");
  const addSource = () => {
    const tgt = addTarget.trim();
    if (!tgt || joins.some((j) => j.dataset === tgt)) return;
    setJoins((js) => [
      ...js,
      addKind === "entity"
        ? { dataset: tgt, source: "entity", app_id: addApp, type: "left", on: [] }
        : { dataset: tgt, type: "left", on: [] },
    ]);
    setAddTarget("");
  };

  // Preview: SAVE first (the preview reads config.model), then read. EDIT-only — in
  // CREATE the dataset doesn't exist yet, so the button is hidden until first save.
  const [preview, setPreview] = React.useState<DatasetPreview | null>(null);
  const modelSpec = () =>
    buildModelSpec(joins, shape, unions, unfold) as DatasetModelSpec | Record<string, never>;
  const previewMut = useMutation({
    mutationFn: async () => {
      await datasetsApi.update(dataset!.id, { model: modelSpec() });
      return datasetsApi.preview(dataset!.id, { page: 1, page_size: 25 });
    },
    onSuccess: (p) => {
      setPreview(p);
      qc.invalidateQueries({ queryKey: ["datasets"] });
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const model = modelSpec();
      if (isCreate) {
        // Canvas-first: the object is BORN here — POST the dataset over the picked
        // primary source+table, then attach the model (join graph + shape) if any.
        const created = await datasetsApi.create({
          name: name.trim(),
          source_id: sourceId!,
          schema_name: schema!,
          table_name: table!,
        });
        if (Object.keys(model).length) await datasetsApi.update(created.id, { model });
        return;
      }
      await datasetsApi.update(dataset!.id, { model });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["datasets"] });
      onClose();
    },
  });
  const canSave = isCreate ? primaryReady && name.trim().length > 0 : true;

  const nativeSelect = "h-8 rounded-md border border-border bg-background px-2 text-sm";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Combine className="h-4 w-4" strokeWidth={1.5} />
            {isCreate
              ? t("datasets.queryBuilderNew", "Nuovo dataset")
              : `${t("datasets.queryBuilderTitle", "Query builder")} — ${dataset!.name}`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {t(
              "datasets.queryBuilderHelp",
              "Aggiungi le sorgenti, trascina da una colonna all'altra per il join, e spunta le colonne da mettere in output (nessuna spunta = tutte). Se le sorgenti sono diverse, il motore le federa in automatico.",
            )}
          </p>

          {/* CREATE mode: pick the PRIMARY relation (source + schema + table) + name it —
              the canvas-first entry (the object is born on Save, no separate dialog). */}
          {isCreate && (
            <div className="grid grid-cols-2 gap-2 rounded-md border border-border bg-elevated/40 p-2 md:grid-cols-4">
              <div className="space-y-1">
                <Label className="text-[11px]">{t("datasets.field.source", "Sorgente")}</Label>
                <Combobox
                  options={(sourcesQ.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
                  value={sourceId}
                  onChange={(v) => {
                    setSourceId(v);
                    setSchema(null);
                    setTable(null);
                  }}
                  loading={sourcesQ.isLoading}
                  placeholder={t("datasets.field.sourcePick", "Scegli una sorgente")}
                  emptyText={t("datasets.field.sourceEmpty", "Nessuna sorgente esterna.")}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">{t("datasets.field.schema", "Schema")}</Label>
                <Combobox
                  options={(schemasQ.data?.items ?? []).map((s) => ({ value: s, label: s }))}
                  value={schema}
                  onChange={(v) => {
                    setSchema(v);
                    setTable(null);
                  }}
                  loading={schemasQ.isLoading}
                  disabled={!sourceId}
                  placeholder="public"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">{t("datasets.field.table", "Tabella")}</Label>
                <Combobox
                  options={(tablesQ.data?.items ?? []).map((tb) => ({ value: tb, label: tb }))}
                  value={table}
                  onChange={setTable}
                  loading={tablesQ.isLoading}
                  disabled={!schema}
                  placeholder={t("datasets.field.tablePick", "Scegli una tabella")}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">{t("datasets.field.name", "Nome")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8" />
              </div>
            </div>
          )}

          {/* add-target toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={nativeSelect}
              value={addKind}
              onChange={(e) => {
                setAddKind(e.target.value as "dataset" | "entity");
                setAddTarget("");
              }}
            >
              <option value="dataset">Dataset</option>
              <option value="entity">{t("datasets.modelAppTable", "Tabella app")}</option>
            </select>
            {addKind === "entity" && (
              <select
                className={nativeSelect}
                value={addApp}
                onChange={(e) => {
                  setAddApp(e.target.value);
                  setAddTarget("");
                }}
              >
                <option value="">{t("datasets.modelApp", "App")}…</option>
                {apps.map((a: AppSummary) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}
            <select
              className={nativeSelect + " min-w-[10rem]"}
              value={addTarget}
              onChange={(e) => setAddTarget(e.target.value)}
              disabled={addKind === "entity" && !addApp}
            >
              <option value="">
                {addKind === "entity"
                  ? `${t("datasets.modelEntityTable", "Tabella")}…`
                  : `${t("datasets.modelTarget", "Dataset da unire")}…`}
              </option>
              {(addKind === "entity" ? (addTablesQ.data ?? []) : targets).map((o) => (
                <option key={o.id} value={o.name}>
                  {o.name}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              disabled={!addTarget || !primaryReady}
              onClick={addSource}
            >
              <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
              {t("datasets.queryBuilderAddSource", "Aggiungi sorgente")}
            </Button>
          </div>

          {/* canvas + shape panel */}
          <div className="flex h-[360px] gap-2">
            <div className="flex-1 rounded-md border border-border" style={{ background: "hsl(var(--background))" }}>
              <ReactFlow
                nodes={rfNodes}
                edges={rfEdges}
                nodeTypes={nodeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onEdgesDelete={onEdgesDelete}
                fitView
                proOptions={{ hideAttribution: true }}
              >
                <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
                <Controls showInteractive={false} />
              </ReactFlow>
            </div>
            <ShapePanel shape={shape} patch={patchShape} columns={allColumns} aliases={outputAliases} />
          </div>

          <UnionsSection
            unions={unions}
            setUnions={setUnions}
            outputCols={outputAliases}
            targets={targets}
            apps={apps}
            datasetIdByName={datasetIdByName}
            primaryReady={primaryReady}
          />

          <UnfoldSection unfold={unfold} setUnfold={setUnfold} columns={outputAliases} />

          {/* preview — EDIT only (needs an existing dataset id; in CREATE, save first) */}
          <div className="flex items-center gap-2">
            {!isCreate ? (
              <Button variant="outline" size="sm" disabled={previewMut.isPending} onClick={() => previewMut.mutate()}>
                {previewMut.isPending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Play className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                )}
                {t("datasets.queryBuilderPreview", "Anteprima")}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                {t("datasets.queryBuilderSaveForPreview", "Salva per vedere l'anteprima.")}
              </span>
            )}
            {preview && (
              <span className="text-xs text-muted-foreground">
                {preview.total} {t("datasets.rows", "righe")}
              </span>
            )}
          </div>
          {previewMut.isError && (
            <Alert tone="danger" title={t("datasets.queryBuilderPreviewFailed", "Anteprima fallita")}>
              <span className="font-mono text-xs">{(previewMut.error as Error).message}</span>
            </Alert>
          )}
          {preview && preview.rows.length > 0 && (
            <div className="max-h-52 overflow-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-elevated">
                  <tr>
                    {Object.keys(preview.rows[0]).map((k) => (
                      <th key={k} className="border-b border-border px-2 py-1 text-left font-medium">
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      {Object.keys(preview.rows[0]).map((k) => (
                        <td key={k} className="px-2 py-1">
                          {r[k] == null ? "" : String(r[k])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {save.isError && (
            <Alert tone="danger" title={t("datasets.modelSaveFailed", "Salvataggio fallito")}>
              <span className="font-mono text-xs">{(save.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel", "Annulla")}
          </Button>
          <Button disabled={save.isPending || !canSave} onClick={() => save.mutate()}>
            {save.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
            {isCreate ? t("datasets.queryBuilderCreate", "Crea dataset") : t("common.save", "Salva")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** UNION section (ADR-0064 P5) — STACK the primary's rows with other relations'. A
 *  union isn't a join (no ON, no key), so it lives in its own section, NOT as a canvas
 *  edge: pick a target (dataset / app table — same OR different source), then map its
 *  columns onto the primary's OUTPUT columns. `union` dedups the stack, `union_all`
 *  keeps all. Cross-source works (the engine materialises + concatenates). */
function UnionsSection({
  unions,
  setUnions,
  outputCols,
  targets,
  apps,
  datasetIdByName,
  primaryReady,
}: {
  unions: DatasetModelUnion[];
  setUnions: React.Dispatch<React.SetStateAction<DatasetModelUnion[]>>;
  outputCols: string[];
  targets: Dataset[];
  apps: AppSummary[];
  datasetIdByName: Map<string, string>;
  primaryReady: boolean;
}) {
  const { t } = useTranslation();
  const nsel = "h-7 rounded border border-border bg-background px-1 text-xs";

  const [kind, setKind] = React.useState<"dataset" | "entity">("dataset");
  const [app, setApp] = React.useState("");
  const [target, setTarget] = React.useState("");
  const appTablesQ = useQuery({
    queryKey: ["apps", app, "tables"],
    queryFn: () => appsApi.listTables(app),
    enabled: kind === "entity" && !!app,
  });

  // Each branch's columns — reuse the join-column fetcher (a union has the same
  // {dataset, source?, app_id?} shape it reads).
  const colQs = useQueries({
    queries: unions.map((u) => ({
      queryKey: ["union-cols", u.dataset, u.source ?? "dataset", u.app_id ?? ""],
      queryFn: () => fetchJoinColumns(u as unknown as DatasetModelJoin, datasetIdByName),
      enabled: u.source !== "entity" || !!u.app_id,
    })),
  });
  const colsByName: Record<string, string[]> = {};
  unions.forEach((u, i) => {
    if (colQs[i]?.data) colsByName[u.dataset] = colQs[i].data!;
  });

  const add = () => {
    const tgt = target.trim();
    if (!tgt) return;
    setUnions((us) => addUnion(us, tgt, kind === "entity" ? { source: "entity", app_id: app } : {}));
    setTarget("");
  };

  return (
    <div className="space-y-2 rounded-md border border-border p-2">
      <div className="flex flex-wrap items-center gap-2">
        <Combine className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-xs font-medium">
          {t("datasets.unionsTitle", "Unioni (impila righe)")}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <select
            className={nsel}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as "dataset" | "entity");
              setTarget("");
            }}
          >
            <option value="dataset">Dataset</option>
            <option value="entity">{t("datasets.modelEntityKind", "Tabella app")}</option>
          </select>
          {kind === "entity" && (
            <select
              className={nsel}
              value={app}
              onChange={(e) => {
                setApp(e.target.value);
                setTarget("");
              }}
            >
              <option value="">{t("datasets.modelApp", "App")}…</option>
              {apps.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
          <select
            className={nsel + " min-w-[9rem]"}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={kind === "entity" && !app}
          >
            <option value="">{t("datasets.unionAddTarget", "Sorgente da impilare")}…</option>
            {(kind === "entity" ? (appTablesQ.data ?? []) : targets).map((o) => (
              <option key={o.id} value={o.name}>
                {o.name}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" disabled={!target || !primaryReady} onClick={add}>
            <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
            {t("datasets.unionAdd", "Unione")}
          </Button>
        </div>
      </div>

      {unions.length === 0 && (
        <p className="text-[10px] italic text-muted-foreground">
          {t(
            "datasets.unionsHint",
            "Impila sotto le righe di un'altra sorgente (anche di un database diverso), mappando le sue colonne su quelle di output.",
          )}
        </p>
      )}

      {unions.map((u, i) => (
        <div key={u.dataset} className="space-y-1 rounded border border-border p-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">{u.dataset}</span>
            <select
              className={nsel}
              value={u.type}
              onChange={(e) =>
                setUnions((us) => setUnionType(us, i, e.target.value as "union" | "union_all"))
              }
            >
              <option value="union_all">{t("datasets.unionAll", "Tieni tutto")}</option>
              <option value="union">{t("datasets.unionDedup", "Senza duplicati")}</option>
            </select>
            <button
              className="ml-auto text-muted-foreground hover:text-danger"
              onClick={() => setUnions((us) => removeUnion(us, i))}
            >
              <Trash2 className="h-3 w-3" strokeWidth={1.5} />
            </button>
          </div>
          {outputCols.length === 0 ? (
            <p className="text-[10px] italic text-muted-foreground">
              {t("datasets.unionPickOutputFirst", "Spunta prima le colonne di output nel canvas.")}
            </p>
          ) : (
            <div className="space-y-0.5">
              {outputCols.map((oc) => {
                const mapped = u.columns.find((c) => c.output === oc)?.field ?? "";
                return (
                  <div key={oc} className="flex items-center gap-1.5">
                    <span className="w-28 shrink-0 truncate text-[11px]">{oc}</span>
                    <span className="text-[10px] text-muted-foreground">←</span>
                    <select
                      className={nsel + " flex-1"}
                      value={mapped}
                      onChange={(e) => setUnions((us) => setUnionColumn(us, i, oc, e.target.value))}
                    >
                      <option value="">— {t("datasets.unionNull", "(vuoto)")}</option>
                      {(colsByName[u.dataset] ?? []).map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** UNFOLD section (ADR-0064 P5 Transformation) — EXPLODE a JSON-array column into one
 *  row per element. Authored in a dedicated panel (a purely post-fetch row transform,
 *  not a join edge). `columns` are the projected OUTPUT column keys to pick from. */
function UnfoldSection({
  unfold,
  setUnfold,
  columns,
}: {
  unfold: DatasetModelUnfold[];
  setUnfold: React.Dispatch<React.SetStateAction<DatasetModelUnfold[]>>;
  columns: string[];
}) {
  const { t } = useTranslation();
  const [field, setField] = React.useState("");
  const nsel = "h-7 rounded border border-border bg-background px-1 text-xs";
  const picked = new Set(unfold.map((u) => u.field));
  const available = columns.filter((c) => !picked.has(c));

  const add = () => {
    if (!field) return;
    setUnfold((u) => addUnfold(u, field));
    setField("");
  };

  return (
    <div className="space-y-2 rounded-md border border-border p-2">
      <div className="flex flex-wrap items-center gap-2">
        <Rows3 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-xs font-medium">
          {t("datasets.unfoldTitle", "Trasforma (espandi elenchi)")}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <select
            className={nsel + " min-w-[9rem]"}
            value={field}
            onChange={(e) => setField(e.target.value)}
          >
            <option value="">{t("datasets.unfoldAddColumn", "Colonna elenco")}…</option>
            {available.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" disabled={!field} onClick={add}>
            <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
            {t("datasets.unfoldAdd", "Espandi")}
          </Button>
        </div>
      </div>

      {unfold.length === 0 && (
        <p className="text-[10px] italic text-muted-foreground">
          {t(
            "datasets.unfoldHint",
            "Spacchetta una colonna che contiene un elenco JSON: una riga per ogni elemento (le chiavi di un oggetto diventano colonne).",
          )}
        </p>
      )}

      {unfold.map((u, i) => (
        <div key={u.field} className="flex items-center gap-2 rounded border border-border/60 p-1.5">
          <Rows3 className="h-3 w-3 shrink-0 text-accent" strokeWidth={1.5} />
          <span className="text-xs font-medium">{u.field}</span>
          <label className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              checked={u.keep_empty !== false}
              onChange={(e) => setUnfold((us) => setUnfoldKeepEmpty(us, i, e.target.checked))}
            />
            {t("datasets.unfoldKeepEmpty", "Tieni le righe con elenco vuoto")}
          </label>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setUnfold((us) => removeUnfold(us, i))}
          >
            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
          </Button>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// APP-PROJECTION MODE (ADR-0063 FE-3 slice B) — the SAME canvas, mounted inline in
// the builder, authoring an app projection as the unified app-flavour `config.model`
// (read by the governed `query_app_dataset` compiler — app-role RLS + field-mask).
// Unlike the global canvas the joins are RELATION-FOLLOW (pick a relation → structural
// ON), so there's no arbitrary drag-ON; the pure model↔config seam is `appModelGraph`.
// =============================================================================

const APP_PROJECTABLE = new Set(["data", "relation", "relation_many"]);
//: App filter op names (the DatasetSpec vocabulary the governed bridge maps to the
//: engine ops: `startswith`→`starts_with`, `not_null`→`is_not_null`, … — see
//: `dataset_bridge._map_op`).
const APP_FILTER_OPS = [
  "eq",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains",
  "startswith",
  "endswith",
  "is_null",
  "not_null",
];
const APP_NO_VALUE_OPS = new Set(["is_null", "not_null"]);

function AppDatasetCanvas({
  appId,
  datasetId,
  canWrite,
}: {
  appId: string;
  datasetId: string;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const dsQ = useQuery({
    queryKey: ["apps", appId, "datasets", datasetId],
    queryFn: () => appsApi.getDataset(appId, datasetId),
  });
  const tablesQ = useQuery({
    queryKey: ["apps", appId, "tables"],
    queryFn: () => appsApi.listTables(appId),
  });
  const tableDetailQs = useQueries({
    queries: (tablesQ.data ?? []).map((tb) => ({
      queryKey: ["apps", appId, "tables", tb.id, "detail"],
      queryFn: () => appsApi.getTable(appId, tb.id),
    })),
  });
  const tableById = React.useMemo(() => {
    const m: Record<string, TableDetail> = {};
    for (const q of tableDetailQs) if (q.data) m[q.data.id] = q.data;
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableDetailQs.map((q) => q.data?.id).join("|")]);

  // Part 2: the app's LINKED global datasets (federation targets) + their fields.
  const linkedQ = useQuery({
    queryKey: ["apps", appId, "linked-datasets"],
    queryFn: () => appsApi.listLinkedDatasets(appId),
  });

  const [name, setName] = React.useState("");
  const [model, setModel] = React.useState<AppEditModel | null>(null);
  const [preview, setPreview] = React.useState<RowsPage | null>(null);
  const [warn, setWarn] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (dsQ.data) {
      setName(dsQ.data.name);
      setModel(appEditFromConfig(dsQ.data.config as Record<string, unknown>, dsQ.data.base_table_id));
      setPreview(null);
    }
  }, [dsQ.data]);

  const baseTableId = dsQ.data?.base_table_id ?? "";
  const aliasTables = React.useMemo(
    () => (model ? resolveAppAliasTables(model, baseTableId, tableById) : {}),
    [model, baseTableId, tableById],
  );
  const tableName = (id?: string | null) =>
    (id && tablesQ.data?.find((tb) => tb.id === id)?.name) || "—";
  const colsForAlias = React.useCallback(
    (alias: string): ColumnOut[] => {
      const tid = aliasTables[alias];
      const cols = tid ? tableById[tid]?.columns ?? [] : [];
      return cols.filter((c) => APP_PROJECTABLE.has(c.kind));
    },
    [aliasTables, tableById],
  );

  // Part 2: the fields of each LINKED dataset currently federated (for the ON + columns).
  const djIds = (model?.dataset_joins ?? []).map((j) => j.dataset_id);
  const dsFieldQs = useQueries({
    queries: djIds.map((id) => ({
      queryKey: ["datasets", id, "fields"],
      queryFn: () => datasetsApi.fields(id),
    })),
  });
  const dsFieldsById = React.useMemo(() => {
    const m: Record<string, string[]> = {};
    dsFieldQs.forEach((q, i) => {
      const id = djIds[i];
      if (id && q.data) m[id] = q.data.map((f) => f.name);
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsFieldQs.map((q) => q.data?.length ?? -1).join("|"), djIds.join("|")]);
  const linkedNameById = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of linkedQ.data ?? []) m[d.id] = d.name;
    return m;
  }, [linkedQ.data]);

  // Build the canvas cards (primary + one per join) + read-only relation edges.
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  const rfNodesRef = React.useRef<Node<QbNodeData>[]>([]);
  rfNodesRef.current = rfNodes;
  React.useEffect(() => {
    if (!model) return;
    const cards: { alias: string; joinIndex: number | null; label: string; type?: "inner" | "left" }[] = [
      { alias: BASE_ALIAS, joinIndex: null, label: tableName(baseTableId) },
      ...model.joins.map((j, i) => ({ alias: j.as, joinIndex: i, label: j.as, type: j.type })),
    ];
    const nodes: Node<QbNodeData>[] = cards.map((card) => {
      const id = card.alias === BASE_ALIAS ? PRIMARY_ID : card.alias;
      const dragged = rfNodesRef.current.find((e) => e.id === id)?.position;
      const node: QbNode = {
        id,
        kind: card.joinIndex === null ? "primary" : "entity",
        label: card.label,
        columns: colsForAlias(card.alias).map((c) => c.name),
        joinIndex: card.joinIndex,
        type: card.type,
      };
      return {
        id,
        type: "qbTable",
        position:
          dragged ??
          (card.joinIndex === null ? { x: 20, y: 20 } : { x: 320, y: 20 + card.joinIndex * 200 }),
        data: {
          node,
          hideHandles: true,
          selected: selectedFieldsFor(model, card.alias),
          onToggleColumn: (field: string) =>
            setModel((m) => (m ? toggleSelectColumn(m, card.alias, field) : m)),
          onType: (ji, type) =>
            setModel((m) =>
              m ? { ...m, joins: m.joins.map((j, k) => (k === ji ? { ...j, type } : j)) } : m,
            ),
          onRemove: (ji) => setModel((m) => (m ? removeJoinCascade(m, m.joins[ji].as) : m)),
        },
      };
    });
    const edges: Edge[] = model.joins.map((j, i) => ({
      id: `rel:${i}`,
      source: j.from === BASE_ALIAS ? PRIMARY_ID : j.from,
      target: j.as,
      sourceHandle: "out",
      targetHandle: "in",
      label: j.via,
      labelStyle: { fontSize: 9 },
      style: { strokeDasharray: "4 2" },
    }));

    // Part 2: FEDERATED linked-dataset nodes on the canvas (+ an edge to the base
    // labelled with the ON) so the federation is VISIBLE, not just in a side panel.
    const leftKeys = federationLeftKeys(model);
    model.dataset_joins.forEach((dj, i) => {
      const id = `fed:${dj.dataset_id}`;
      const dragged = rfNodesRef.current.find((e) => e.id === id)?.position;
      const fedData: FederatedNodeData = {
        datasetId: dj.dataset_id,
        label: linkedNameById[dj.dataset_id] ?? dj.dataset_id,
        fields: dsFieldsById[dj.dataset_id] ?? [],
        picked: new Set(dj.columns.map((c) => c.field)),
        type: dj.type,
        on: dj.on.length ? dj.on : [{ left: "", right: "" }],
        leftKeys,
        canWrite,
        onToggleCol: (field) =>
          setModel((m) => (m ? toggleDatasetColumn(m, dj.dataset_id, linkedNameById[dj.dataset_id] ?? "ds", field) : m)),
        onType: (type) => setModel((m) => (m ? patchDatasetJoin(m, dj.dataset_id, { type }) : m)),
        onRemove: () => setModel((m) => (m ? removeDatasetJoin(m, dj.dataset_id) : m)),
        onPatchOn: (on) => setModel((m) => (m ? patchDatasetJoin(m, dj.dataset_id, { on }) : m)),
      };
      nodes.push({
        id,
        type: "qbFederated",
        position: dragged ?? { x: 640, y: 20 + i * 260 },
        data: fedData as unknown as QbNodeData,
      });
      const onLabel = dj.on
        .filter((o) => o.left && o.right)
        .map((o) => `${o.left} = ${o.right}`)
        .join(", ");
      edges.push({
        id: `fededge:${dj.dataset_id}`,
        source: PRIMARY_ID,
        target: id,
        label: onLabel || "(abbina…)",
        labelStyle: { fontSize: 9 },
        style: { stroke: "hsl(var(--accent))", strokeDasharray: "2 2" },
      });
    });

    setRfNodes(nodes);
    setRfEdges(edges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, tableById, baseTableId, dsFieldsById, linkedNameById, canWrite]);

  // Add-target: relation-follow (a `from` alias + a relation `via` on its table).
  const [addFrom, setAddFrom] = React.useState<string>(BASE_ALIAS);
  // Join options for the `from` alias: FORWARD relations (a belongs-to column ON its
  // table) + REVERSE relations (any OTHER app table whose relation points back at it —
  // a has-many, "bring in the children that reference this parent"). Each option encodes
  // its (direction, via, target) in the value so the select stays a plain string.
  const joinOptions = React.useMemo(() => {
    const out: {
      value: string;
      label: string;
      direction: "forward" | "reverse";
      via: string;
      target?: string;
    }[] = [];
    const fromTid = aliasTables[addFrom];
    if (!fromTid) return out;
    for (const c of tableById[fromTid]?.columns ?? []) {
      if (c.kind === "relation") {
        out.push({
          value: `f:${c.name}`,
          label: `→ ${tableName(c.config?.target_table as string)} (via ${c.name})`,
          direction: "forward",
          via: c.name,
        });
      }
    }
    for (const t of tablesQ.data ?? []) {
      if (t.id === fromTid) continue;
      for (const c of tableById[t.id]?.columns ?? []) {
        if (c.kind === "relation" && (c.config?.target_table as string) === fromTid) {
          out.push({
            value: `r:${t.id}:${c.name}`,
            label: `↩ ${t.name} (via ${c.name})`,
            direction: "reverse",
            via: c.name,
            target: t.id,
          });
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aliasTables, addFrom, tableById, tablesQ.data]);
  const aliasOptions = model ? [BASE_ALIAS, ...model.joins.map((j) => j.as)] : [BASE_ALIAS];

  const previewMut = useMutation({
    mutationFn: async () => {
      await appsApi.updateDataset(appId, datasetId, { name: name.trim(), config: appEditToConfig(model!) });
      return appsApi.previewDataset(appId, datasetId, { page: 1, page_size: 50 });
    },
    onSuccess: (p) => {
      setPreview(p);
      qc.invalidateQueries({ queryKey: ["apps", appId, "datasets"] });
    },
  });
  const save = useMutation({
    mutationFn: () =>
      appsApi.updateDataset(appId, datasetId, { name: name.trim(), config: appEditToConfig(model!) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "datasets"] }),
  });

  // A projection MUST project ≥1 column (the backend `columns` min-length) — gate both
  // save + preview on it with a friendly message rather than a raw 422.
  const guard = (run: () => void) => {
    if (!model) return;
    if (model.select.length === 0) {
      setWarn(t("datasets.appNoColumns", "Seleziona almeno una colonna da mettere in output."));
      return;
    }
    setWarn(null);
    run();
  };

  if (dsQ.isError) {
    return (
      <Alert tone="danger" title={t("apps.datasetLoadFailed", "Proiezione non caricata")}>
        <span className="font-mono text-xs">{(dsQ.error as Error).message}</span>
      </Alert>
    );
  }
  if (!model || !dsQ.data) {
    return <p className="text-sm text-muted-foreground">{t("common.loading", "Caricamento…")}</p>;
  }

  const nativeSelect = "h-8 rounded-md border border-border bg-background px-2 text-sm";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={name}
          disabled={!canWrite}
          className="h-8 w-56 font-semibold"
          onChange={(e) => setName(e.target.value)}
        />
        <span className="text-xs text-muted-foreground">
          {t("apps.baseTable", "Tabella base")}: <strong>{tableName(baseTableId)}</strong>
        </span>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={previewMut.isPending}
            onClick={() => guard(() => previewMut.mutate())}
          >
            {previewMut.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <Play className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
            )}
            {t("apps.preview", "Anteprima")}
          </Button>
          {canWrite && (
            <Button size="sm" disabled={save.isPending} onClick={() => guard(() => save.mutate())}>
              {save.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
              {t("common.save", "Salva")}
            </Button>
          )}
        </div>
      </div>

      {canWrite && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{t("apps.joinFrom", "da")}</span>
          <select className={nativeSelect} value={addFrom} onChange={(e) => setAddFrom(e.target.value)}>
            {aliasOptions.map((a) => (
              <option key={a} value={a}>
                {a === BASE_ALIAS ? tableName(baseTableId) : a}
              </option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">
            {t("apps.joinVia", "segui relazione")}
          </span>
          <select
            className={nativeSelect + " min-w-[13rem]"}
            value=""
            disabled={joinOptions.length === 0}
            onChange={(e) => {
              const opt = joinOptions.find((o) => o.value === e.target.value);
              if (!opt) return;
              setModel((m) =>
                m
                  ? {
                      ...m,
                      joins: addRelationJoin(
                        m.joins,
                        addFrom,
                        opt.via,
                        opt.direction === "reverse"
                          ? { direction: "reverse", target: opt.target }
                          : undefined,
                      ),
                    }
                  : m,
              );
            }}
          >
            <option value="">
              {joinOptions.length === 0
                ? t("apps.noRelations", "nessuna relazione")
                : `${t("apps.pickRelation", "scegli")}…`}
            </option>
            {joinOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <AppFederationSection
        model={model}
        linked={linkedQ.data ?? []}
        canWrite={canWrite}
        setModel={setModel}
      />

      <AppUnionUnfoldSection
        model={model}
        linked={linkedQ.data ?? []}
        canWrite={canWrite}
        setModel={setModel}
      />

      {(warn || save.isError || previewMut.isError) && (
        <Alert tone="danger" title={t("apps.datasetSaveFailed", "Proiezione non valida")}>
          <span className="font-mono text-xs">
            {warn || ((save.error || previewMut.error) as Error)?.message}
          </span>
        </Alert>
      )}

      <div className="flex h-[340px] gap-2">
        <div
          className="flex-1 rounded-md border border-border"
          style={{ background: "hsl(var(--background))" }}
        >
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
        <AppShapePanel model={model} patch={(p) => setModel((m) => (m ? { ...m, ...p } : m))} />
      </div>

      {preview && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            {preview.total} {t("datasets.rows", "righe")}
          </p>
          {preview.rows.length > 0 && (
            <div className="max-h-52 overflow-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-elevated">
                  <tr>
                    {Object.keys(preview.rows[0]).map((k) => (
                      <th key={k} className="border-b border-border px-2 py-1 text-left font-medium">
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      {Object.keys(preview.rows[0]).map((k) => (
                        <td key={k} className="px-2 py-1">
                          {r[k] == null ? "" : String(r[k])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Part 2 — the "Dataset collegati (federazione)" authoring section. An app projection
 *  can JOIN a global Dataset the app LINKED (`app_dataset_links`); the engine federates
 *  it + applies the role's per-role N4 grants at read. The operator picks a linked
 *  dataset, matches an app-projection output column to a dataset field (the ON), and
 *  ticks which dataset fields to project. */
function AppFederationSection({
  model,
  linked,
  canWrite,
  setModel,
}: {
  model: AppEditModel;
  linked: LinkedResource[];
  canWrite: boolean;
  setModel: React.Dispatch<React.SetStateAction<AppEditModel | null>>;
}) {
  const { t } = useTranslation();
  const nsel = "h-7 rounded border border-border bg-background px-1 text-xs";
  const usedIds = new Set(model.dataset_joins.map((j) => j.dataset_id));
  const addable = linked.filter((d) => !usedIds.has(d.id));

  if (model.dataset_joins.length === 0 && (!canWrite || linked.length === 0)) return null;

  return (
    <div className="space-y-2 rounded-md border border-border p-2">
      <div className="flex items-center gap-2">
        <Combine className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-xs font-medium">
          {t("datasets.appFederationTitle", "Dataset collegati (federazione)")}
        </span>
        {canWrite && (
          <select
            className={nsel + " ml-auto min-w-[12rem]"}
            value=""
            disabled={addable.length === 0}
            onChange={(e) => {
              const id = e.target.value;
              if (id) setModel((m) => (m ? addDatasetJoin(m, id) : m));
            }}
          >
            <option value="">
              {addable.length === 0
                ? t("datasets.appNoLinkedDatasets", "nessun dataset collegato")
                : `+ ${t("datasets.appAddLinkedDataset", "aggiungi dataset collegato")}…`}
            </option>
            {addable.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <p className="text-[10px] italic text-muted-foreground">
        {model.dataset_joins.length === 0
          ? t(
              "datasets.appFederationHint",
              "Unisci un dataset esterno che l'app ha collegato; i grant per-ruolo (righe/campi) sono applicati automaticamente in lettura.",
            )
          : t(
              "datasets.appFederationOnCanvas",
              "I dataset collegati compaiono come nodi sul canvas: lì scegli l'abbinamento e le colonne.",
            )}
      </p>
    </div>
  );
}

/** UNION (stack a linked dataset) + UNFOLD (explode a JSON-array column) authoring for an
 *  app projection (ADR-0064 P5, app-plane). Union branches are LINKED global datasets only
 *  (N4-governed); unfold explodes an OUTPUT column into rows. */
function AppUnionUnfoldSection({
  model,
  linked,
  canWrite,
  setModel,
}: {
  model: AppEditModel;
  linked: LinkedResource[];
  canWrite: boolean;
  setModel: React.Dispatch<React.SetStateAction<AppEditModel | null>>;
}) {
  const { t } = useTranslation();
  const nsel = "h-7 rounded border border-border bg-background px-1 text-xs";
  const nameById = React.useMemo(
    () => Object.fromEntries(linked.map((d) => [d.id, d.name])),
    [linked],
  );
  const outCols = outputKeys(model);
  const usedUnion = new Set(model.unions.map((u) => u.dataset_id));
  const addableUnion = linked.filter((d) => !usedUnion.has(d.id));
  const usedUnfold = new Set(model.unfold.map((u) => u.field));
  const addableUnfold = outCols.filter((k) => !usedUnfold.has(k));

  // Fields of each linked dataset currently unioned (for the column map).
  const unionFieldQs = useQueries({
    queries: model.unions.map((u) => ({
      queryKey: ["datasets", u.dataset_id, "fields"],
      queryFn: () => datasetsApi.fields(u.dataset_id),
    })),
  });
  const unionFieldsById: Record<string, string[]> = {};
  model.unions.forEach((u, i) => {
    unionFieldsById[u.dataset_id] = (unionFieldQs[i]?.data ?? []).map((f) => f.name);
  });

  if (
    model.unions.length === 0 &&
    model.unfold.length === 0 &&
    (!canWrite || (linked.length === 0 && outCols.length === 0))
  ) {
    return null;
  }

  return (
    <div className="space-y-2 rounded-md border border-border p-2">
      {/* UNION */}
      <div className="flex flex-wrap items-center gap-2">
        <Combine className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-xs font-medium">
          {t("datasets.appUnionTitle", "Unioni (impila righe)")}
        </span>
        {canWrite && (
          <select
            className={nsel + " ml-auto min-w-[12rem]"}
            value=""
            disabled={addableUnion.length === 0}
            onChange={(e) => {
              const id = e.target.value;
              if (id) setModel((m) => (m ? appAddUnion(m, id) : m));
            }}
          >
            <option value="">
              {addableUnion.length === 0
                ? t("datasets.appNoLinkedDatasets", "nessun dataset collegato")
                : `+ ${t("datasets.appAddUnion", "impila un dataset collegato")}…`}
            </option>
            {addableUnion.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {model.unions.length === 0 && (
        <p className="text-[10px] italic text-muted-foreground">
          {t(
            "datasets.appUnionHint",
            "Impila sotto le righe di un dataset collegato, mappando i suoi campi sulle colonne di output (i grant per-ruolo sono applicati automaticamente).",
          )}
        </p>
      )}
      {model.unions.map((u) => (
        <div key={u.dataset_id} className="space-y-1 rounded border border-border/60 p-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">{nameById[u.dataset_id] ?? u.dataset_id}</span>
            <select
              className={nsel + " ml-auto"}
              value={u.type}
              disabled={!canWrite}
              onChange={(e) =>
                setModel((m) =>
                  m ? appSetUnionType(m, u.dataset_id, e.target.value as "union" | "union_all") : m,
                )
              }
            >
              <option value="union_all">{t("datasets.unionKeepAll", "Tieni tutto")}</option>
              <option value="union">{t("datasets.unionDedup", "Senza duplicati")}</option>
            </select>
            {canWrite && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setModel((m) => (m ? appRemoveUnion(m, u.dataset_id) : m))}
              >
                <Trash2 className="h-3 w-3" strokeWidth={1.5} />
              </Button>
            )}
          </div>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1">
            {outCols.map((oc) => {
              const mapped = u.columns.find((c) => c.output === oc)?.field ?? "";
              return (
                <React.Fragment key={oc}>
                  <span className="truncate text-[11px] text-muted-foreground">{oc}</span>
                  <span className="text-[10px] text-muted-foreground">←</span>
                  <select
                    className={nsel}
                    value={mapped}
                    disabled={!canWrite}
                    onChange={(e) =>
                      setModel((m) =>
                        m ? appSetUnionColumn(m, u.dataset_id, oc, e.target.value) : m,
                      )
                    }
                  >
                    <option value="">—</option>
                    {(unionFieldsById[u.dataset_id] ?? []).map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      ))}

      {/* UNFOLD */}
      <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-2">
        <Rows3 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-xs font-medium">
          {t("datasets.appUnfoldTitle", "Trasforma (espandi elenchi)")}
        </span>
        {canWrite && (
          <select
            className={nsel + " ml-auto min-w-[9rem]"}
            value=""
            disabled={addableUnfold.length === 0}
            onChange={(e) => {
              const f = e.target.value;
              if (f) setModel((m) => (m ? appAddUnfold(m, f) : m));
            }}
          >
            <option value="">{t("datasets.unfoldAddColumn", "Colonna elenco")}…</option>
            {addableUnfold.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        )}
      </div>
      {model.unfold.length === 0 && (
        <p className="text-[10px] italic text-muted-foreground">
          {t(
            "datasets.appUnfoldHint",
            "Spacchetta una colonna che contiene un elenco JSON: una riga per elemento (le chiavi di un oggetto diventano colonne).",
          )}
        </p>
      )}
      {model.unfold.map((u) => (
        <div key={u.field} className="flex items-center gap-2 rounded border border-border/60 p-1.5">
          <Rows3 className="h-3 w-3 shrink-0 text-accent" strokeWidth={1.5} />
          <span className="text-xs font-medium">{u.field}</span>
          <label className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              checked={u.keep_empty !== false}
              disabled={!canWrite}
              onChange={(e) =>
                setModel((m) => (m ? appSetUnfoldKeepEmpty(m, u.field, e.target.checked) : m))
              }
            />
            {t("datasets.unfoldKeepEmpty", "Tieni le righe con elenco vuoto")}
          </label>
          {canWrite && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setModel((m) => (m ? appRemoveUnfold(m, u.field) : m))}
            >
              <Trash2 className="h-3 w-3" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

type AppShapeTab = "filters" | "group" | "sort";

/** The Filtri · Raggruppa · Ordina panel for an app projection — authors the shape
 *  over the projected OUTPUT column keys (`as`), mirroring the global `ShapePanel`. */
function AppShapePanel({
  model,
  patch,
}: {
  model: AppEditModel;
  patch: (p: Partial<AppEditModel>) => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState<AppShapeTab>("filters");
  const keys = outputKeys(model);
  const nsel = "h-7 rounded border border-border bg-background px-1 text-xs";
  const tabs: { id: AppShapeTab; label: string }[] = [
    { id: "filters", label: t("datasets.qbTabFilters", "Filtri") },
    { id: "group", label: t("datasets.qbTabGroup", "Raggruppa") },
    { id: "sort", label: t("datasets.qbTabSort", "Ordina") },
  ];

  return (
    <div className="flex w-64 shrink-0 flex-col rounded-md border border-border">
      <div className="flex shrink-0 border-b border-border">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            className={
              "flex-1 border-b-2 px-1 py-1.5 text-[11px] " +
              (tab === tb.id ? "border-accent font-medium" : "border-transparent text-muted-foreground")
            }
            onClick={() => setTab(tb.id)}
          >
            {tb.label}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 space-y-1.5 overflow-auto p-2 text-xs">
        {keys.length === 0 && (
          <p className="text-[10px] italic text-muted-foreground">
            {t("datasets.appPickColumnsFirst", "Spunta prima qualche colonna nel canvas.")}
          </p>
        )}

        {tab === "filters" && keys.length > 0 && (
          <>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-muted-foreground">
                {t("datasets.qbMatch", "Corrispondenza")}
              </span>
              <select
                className={nsel}
                value={model.match}
                onChange={(e) => patch({ match: e.target.value as "all" | "any" })}
              >
                <option value="all">{t("datasets.qbMatchAll", "Tutte (E)")}</option>
                <option value="any">{t("datasets.qbMatchAny", "Almeno una (O)")}</option>
              </select>
            </div>
            {model.filters.map((f, i) => (
              <div key={i} className="flex items-center gap-1">
                <select
                  className={nsel + " flex-1"}
                  value={f.field}
                  onChange={(e) =>
                    patch({ filters: model.filters.map((x, k) => (k === i ? { ...x, field: e.target.value } : x)) })
                  }
                >
                  <option value="">—</option>
                  {keys.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
                <select
                  className={nsel}
                  value={f.op}
                  onChange={(e) =>
                    patch({
                      filters: model.filters.map((x, k) =>
                        k === i
                          ? {
                              ...x,
                              op: e.target.value,
                              ...(APP_NO_VALUE_OPS.has(e.target.value) ? { value: undefined } : {}),
                            }
                          : x,
                      ),
                    })
                  }
                >
                  {APP_FILTER_OPS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                {!APP_NO_VALUE_OPS.has(f.op) && (
                  <Input
                    className="h-7 w-16"
                    value={(f.value as string | number | undefined) ?? ""}
                    onChange={(e) =>
                      patch({ filters: model.filters.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)) })
                    }
                  />
                )}
                <button
                  className="text-muted-foreground hover:text-danger"
                  onClick={() => patch({ filters: model.filters.filter((_, k) => k !== i) })}
                >
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                patch({ filters: [...model.filters, { field: keys[0], op: "eq", value: "" } as AppModelFilter] })
              }
            >
              <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
              {t("datasets.qbAddFilter", "Filtro")}
            </Button>
          </>
        )}

        {tab === "group" && keys.length > 0 && (
          <>
            <p className="text-[10px] font-medium text-muted-foreground">
              {t("datasets.qbGroupBy", "Raggruppa per")}
            </p>
            {keys.map((k) => {
              const on = model.group_by.some((g) => g.field === k);
              return (
                <label key={k} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      patch({
                        group_by: on
                          ? model.group_by.filter((g) => g.field !== k)
                          : [...model.group_by, { field: k }],
                      })
                    }
                  />
                  <span className="flex-1 truncate">{k}</span>
                </label>
              );
            })}
            <p className="pt-1 text-[10px] font-medium text-muted-foreground">
              {t("datasets.qbAggregations", "Aggregazioni")}
            </p>
            {model.aggregations.map((a, i) => (
              <div key={i} className="flex items-center gap-1">
                <select
                  className={nsel}
                  value={a.fn}
                  onChange={(e) =>
                    patch({ aggregations: model.aggregations.map((x, k) => (k === i ? { ...x, fn: e.target.value } : x)) })
                  }
                >
                  {AGG_FNS.map((fn) => (
                    <option key={fn} value={fn}>
                      {fn}
                    </option>
                  ))}
                </select>
                {a.fn !== "count" && (
                  <select
                    className={nsel + " flex-1"}
                    value={a.field ?? ""}
                    onChange={(e) =>
                      patch({
                        aggregations: model.aggregations.map((x, k) =>
                          k === i ? { ...x, field: e.target.value || undefined } : x,
                        ),
                      })
                    }
                  >
                    <option value="">—</option>
                    {keys.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                )}
                <Input
                  className="h-7 w-16"
                  placeholder="alias"
                  value={a.as}
                  onChange={(e) =>
                    patch({ aggregations: model.aggregations.map((x, k) => (k === i ? { ...x, as: e.target.value } : x)) })
                  }
                />
                <button
                  className="text-muted-foreground hover:text-danger"
                  onClick={() => patch({ aggregations: model.aggregations.filter((_, k) => k !== i) })}
                >
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                patch({ aggregations: [...model.aggregations, { fn: "count", as: "conteggio" } as AppModelAgg] })
              }
            >
              <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
              {t("datasets.qbAddAgg", "Aggregazione")}
            </Button>
          </>
        )}

        {tab === "sort" && keys.length > 0 && (
          <>
            <p className="text-[10px] font-medium text-muted-foreground">
              {t("datasets.qbOrderBy", "Ordina per")}
            </p>
            {model.order_by.map((o, i) => (
              <div key={i} className="flex items-center gap-1">
                <select
                  className={nsel + " flex-1"}
                  value={o.by}
                  onChange={(e) =>
                    patch({ order_by: model.order_by.map((x, k) => (k === i ? { ...x, by: e.target.value } : x)) })
                  }
                >
                  <option value="">—</option>
                  {keys.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
                <select
                  className={nsel}
                  value={o.dir}
                  onChange={(e) =>
                    patch({
                      order_by: model.order_by.map((x, k) =>
                        k === i ? { ...x, dir: e.target.value as "asc" | "desc" } : x,
                      ),
                    })
                  }
                >
                  <option value="asc">ASC</option>
                  <option value="desc">DESC</option>
                </select>
                <button
                  className="text-muted-foreground hover:text-danger"
                  onClick={() => patch({ order_by: model.order_by.filter((_, k) => k !== i) })}
                >
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patch({ order_by: [...model.order_by, { by: keys[0], dir: "asc" }] })}
            >
              <Plus className="mr-1 h-3 w-3" strokeWidth={1.5} />
              {t("datasets.qbAddSort", "Ordinamento")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
